import { AxiosError } from "axios";
import { useRef, useState } from "react";
import { useImproveNotePublic } from "~/api/notes/notes";
import { NOTE_IMPROVEMENT_PROMPT, TEXT_MARKER } from "~/constants/prompts";
import logger from "~/utils/logger";

export const useTranscribing = () => {
  const [textTranscribing, setTextTranscribing] = useState("");

  const { mutateAsync: improveNotePublic } = useImproveNotePublic();

  const [isProcessingQueue, setIsProcessingQueue] = useState(false);
  const isProcessingQueueRef = useRef(false);

  const pendingTextRef = useRef("");

  const currentTextRef = useRef("");

  const processQueue = async () => {
    if (isProcessingQueueRef.current) return;

    isProcessingQueueRef.current = true;
    setIsProcessingQueue(true);

    if (pendingTextRef.current.trim()) {
      const pending = pendingTextRef.current.trim();

      const combinedText = currentTextRef.current
        ? `${currentTextRef.current} ${pending}`
        : pending;

      try {
        const improvedResult = await improveNotePublic({
          data: {
            improvementPrompt: `${NOTE_IMPROVEMENT_PROMPT}${TEXT_MARKER}"${combinedText}"`,
            model: "gemini-2.0-flash-lite",
          },
        });

        currentTextRef.current = improvedResult.improvedNote;
        setTextTranscribing(improvedResult.improvedNote);
      } catch (err) {
        logger.error("Auto improvement failed:", err);

        let errorMessage = "Auto improvement failed. Please try again.";

        if (err instanceof AxiosError) {
          const status = err.response?.status;
          const responseData = err.response?.data;

          if (status === 400) {
            const message = responseData?.error || responseData?.message || "";
            if (message) {
              errorMessage = message;
            }
          } else if (status === 429) {
            const message = responseData?.error || responseData?.message || "";
            if (message) {
              errorMessage = message;
            } else {
              errorMessage = "Too many requests. Please try again later.";
            }
          }
        }
        logger.error("Error message:", errorMessage);

        pendingTextRef.current = pendingTextRef.current
          ? `${pending} ${pendingTextRef.current}`
          : pending;

        return errorMessage;
      } finally {
        isProcessingQueueRef.current = false;
        pendingTextRef.current = "";

        setIsProcessingQueue(false);
      }
    }
  };

  const handleAutoImproveChunk = async (transcribedText: string) => {
    if (!transcribedText?.trim()) return;

    pendingTextRef.current = pendingTextRef.current
      ? `${pendingTextRef.current} ${transcribedText}`
      : transcribedText;

    if (!isProcessingQueueRef.current) {
      return await processQueue();
    }
  };

  const resetTranscribing = () => {
    pendingTextRef.current = "";
    currentTextRef.current = "";
    setTextTranscribing("");
    isProcessingQueueRef.current = false;
    setIsProcessingQueue(false);
  };

  const updateTextTranscribing = (newText: string) => {
    setTextTranscribing(newText);
    currentTextRef.current = newText;
  };

  return {
    textTranscribing: textTranscribing,
    isProcessingQueue,
    handleAutoImproveChunk,
    resetTranscribing,
    updateTextTranscribing,
  };
};
